import comm from "csi/comm";
import reflection from "csi/reflection";
import { rulePasses } from "csi/service/ruleService";
import StoreCache from "csi/store/Cache";
import { AccessRule, C8Node, cnl, cnlPath } from "csi/typings";
import { extendUrlPath, pathCreate } from "csi/utility/pathUtility";
import csiView from "csi/view";
import { ViewWidget } from "csi/view/Widget";
import _ from "lodash";
import { action, autorun, computed, makeObservable, observable } from "mobx";
import { getLocationHash, LocationItemState, LOCATION_TAB } from "csi/utility/routerUtility";
import { getViewAccessRights } from "./viewUtils";
import WorkingCache from "./WorkingCache";
import { ReactionStore } from "csi/utility/mobxUtility";

// TODO: One potential improvement is to not store _selectedItem explicitly, but rather compute it from selectedChild;
// selectedChild could do the validation, rather than using autorun to do it;
// instead, autorun would just watch selectedItem computed value and update selected property of each item;
// not sure if that is any better than what we have currently though

declare type LayoutItemAccessFunction = (node: C8Node, cache: WorkingCache) => boolean;

let uniqueIdCtr = 0;

export interface LayoutItemOptions {
	/** additional function used to determine visibility of item */
	readonly accessFunction?: LayoutItemAccessFunction;

	/** force view to not be editable */
	readonly forceReadOnly?: boolean;

	/** path to item */
	readonly path?: string;

	/** rules to test for access to item */
	readonly rules?: AccessRule[];

	/** switchboard to apply to view (used in conjunction with targetType) */
	readonly switchboard?: any;

	/** node type owning view; use with switchboard where root node is determined by query rather than fixed url or path */
	readonly targetType?: string;

	/** url for global node owning view */
	readonly url?: cnl;
}

export class LayoutItem {
	@observable private _collapsed = false;
	@observable private _helpUseRootType = false;
	@observable private _name: string;
	@observable private _selected = false;
	@observable private _viewOwnerType: string;

	/** cache for item */
	readonly cache: WorkingCache;

	/** child items in layout */
	readonly children = observable.array<LayoutItem>(null, { deep: false });

	/** header view identifier to insert before any child tabs */
	headerViewId?: string;

	/** view identifier */
	readonly id: string;

	/** last visit tab id of each parent item */
	private lastVisitedTabId: string;

	/** hash when item was navigated away from; used to restore hash when navigate back to item */
	locationState?: LocationItemState[];

	/** item options */
	readonly options?: LayoutItemOptions;

	/** parent item */
	readonly parent?: LayoutItem;

	/** unique id for item */
	readonly uniqueId: string;

	/** indicates if non-leaf item is collapsed */
	get collapsed(): boolean {
		return this._collapsed !== false;
	}

	/** leaf item to select under this item, or this if no visible children */
	@computed get childToSelect(): LayoutItem {
		const { lastVisitedTab, selectedChild, visibleChildren } = this;
		const item = selectedChild || lastVisitedTab || visibleChildren[0];

		return item?.childToSelect || this;
	}

	/** number of levels of items under this item; a return value of 0 indicates item is at root of tree */
	get depth(): number {
		const { parent } = this;
		return (parent ? parent.depth : -1) + 1;
	}

	/** type to associate with help for this view */
	get helpType(): string {
		const { node, viewOwnerType, _helpUseRootType } = this;
		return _helpUseRootType ? node?.$Type : viewOwnerType;
	}

	/** the last visit tab */
	get lastVisitedTab(): LayoutItem | null {
		const targetTab = this.children.find((child) => child.id === this.lastVisitedTabId);

		return targetTab?.visible ? targetTab : null;
	}

	/** indicates if item has no visible children (leaf of item tree) */
	get leaf(): boolean {
		return !this.visibleChildren.length;
	}

	/** name of view */
	get name(): string {
		return this._name;
	}

	/** node owning view */
	@computed get node(): C8Node | null {
		return this.cache.getUrlPathNode(this.urlPath);
	}

	/** root node type */
	@computed get rootNodeType(): string {
		const { node, parent, viewOwnerType } = this;
		const type = parent?.rootNodeType || node?.$Type || viewOwnerType;
		const rc = reflection.getClass(type);

		// see ViewStoreBuilder#_process for similar code when setting _typeKey
		return rc.isRevision() ? rc.getMasterClass().getType() : type;
	}

	/** url path pairs needed for item rules */
	get rulePaths(): cnlPath[] {
		// rules are relative to the parent item; if at the root level, then they are relative to the root url
		const { parent, options } = this;
		const baseUrlPath = parent?.urlPath ?? [options.url, ""];
		const urlPaths = [];

		options?.rules?.forEach((rule) => {
			if (rule.path) {
				urlPaths.push(extendUrlPath(baseUrlPath, rule.path));
			}
		});

		return urlPaths;
	}

	/** indicate if the container of the item can be able to scroll vertically */
	get scrollableVertically(): boolean {
		// the table view won't have the id, because we pass tabsView?.m_id as the id during layout store initialization
		return !this.id && !!this.headerViewId;
	}

	/** indicates if item is selected - can only be one selected among parent's children */
	get selected(): boolean {
		return this._selected;
	}

	/** selected child item (may or may not be currently visible as a parent item may have selected a different child) */
	@computed get selectedChild(): LayoutItem | null {
		return this.children.find((item) => item.selected);
	}

	/** url path pair needed to get node for item */
	get urlPath(): cnlPath {
		const { options, parent } = this;
		const itemPath = options?.path;

		// if this item has a URL, then doesn't matter what parent item's urlPath is
		if (options?.url) {
			return [options?.url, itemPath];
		}

		// url must exist for root items, so if not here, then must have parent
		console.assert(!!parent);

		// get urlPath from parent and combine with any path on this item
		const { urlPath } = parent;
		const [url, path] = urlPath;
		return [url, pathCreate(path, itemPath)];
	}

	/** view widget for item if already visited */
	get view(): ViewWidget | undefined {
		const existingViews = csiView.getAllViews(this.id);
		return existingViews?.find((view) => view.layoutItem === this);
	}

	/** node type that owns view - if not set, then view was not found */
	get viewOwnerType(): string {
		return this._viewOwnerType;
	}

	/** item is visible */
	@computed get visible(): boolean {
		const { cache, children, headerViewId, id, node, options, parent, rootNodeType, viewOwnerType, visibleChildren } = this;

		// root item is not displayed, but mark as visible so those under it are viewed as visible
		if (!parent) {
			return true;
		}

		// item is valid if there are no children (not a tab view), or the tab view has a header (always visible) or visible children
		let valid = node &&
			viewOwnerType &&
			(!children.length || headerViewId || visibleChildren.length) &&
			!!getViewAccessRights(rootNodeType, viewOwnerType, id, null, node);

		if (options && valid) {
			// check access function and rules
			const { accessFunction, rules } = options;

			if (accessFunction) {
				valid = accessFunction(node, cache);
			}

			if (valid && rules?.length) {
				// rules are relative to the parent node, rather than their own
				const { node } = parent;
				valid = rules.every((rule) => rulePasses(rule, node, cache));
			}
		}

		return !!valid;
	}

	/** list of visible child items */
	@computed get visibleChildren(): LayoutItem[] {
		return this.children.filter((item) => item.visible);
	}

	/** indicates if item and all parents are visible */
	@computed get visibleWithParents(): boolean {
		let item: LayoutItem = this;

		while (item?.visible) {
			item = item.parent;
		}

		return !item;
	}

	/** add child layout item */
	@action addChild = (id: string, options?: LayoutItemOptions): LayoutItem => {
		const { cache, children } = this;
		const item = new LayoutItem(id, cache, options, this);
		children.push(item);
		return item;
	};

	/** depth-first traversal of item tree to collect all into a single array */
	collectItems = (all: LayoutItem[]): void => {
		all.push(this);
		this.children.forEach((item) => item.collectItems(all));
	};

	/** resolve view by settings its name and owner type */
	@action resolveView = (name: string, viewOwnerType: string, helpUseRootType = false): void => {
		this._name = name;
		this._viewOwnerType = viewOwnerType;
		this._helpUseRootType = helpUseRootType;
	};

	/** select item */
	@action select = (): void => {
		if (this._selected) {
			return;
		}

		const { id, parent } = this;

		if (parent) {
			// reset previous selection
			const { selectedChild } = parent;
			if (selectedChild) {
				selectedChild.unselect();
			}

			// select this item
			this._selected = true;

			// save the last visit tab on parent
			parent.lastVisitedTabId = id;
		}
	};

	/** collapse */
	@action setCollapsed = (collapsed: boolean): void => {
		console.assert(!this.leaf);
		this._collapsed = collapsed;
	};

	/** mark item as not selected, as well as any children */
	@action unselect = (): void => {
		if (!this._selected) {
			return;
		}

		this._selected = false;
		this.selectedChild?.unselect();
	};

	constructor(id: string, cache: WorkingCache, options?: LayoutItemOptions, parent?: LayoutItem) {
		makeObservable(this);
		this.cache = cache;
		this.id = id;
		this.options = options;
		this.parent = parent;
		this.uniqueId = `LayoutItem${++uniqueIdCtr}`;
	}
}

export default class LayoutStore extends ReactionStore {
	@observable private _readOnly: boolean;

	/** selected item to view */
	@observable.ref private _selectedItem: LayoutItem;

	/** cache holding nodes used in layout */
	readonly cache: WorkingCache;

	/** key in local storage holding last selection */
	readonly historyKey: string;

	/** indicates if left pane should be visible */
	@observable leftPaneOpen = true;

	/** location parameters used to generate location hash for layout items */
	readonly locationParams?: Record<string, string>;

	/** root item of layout (not displayed) */
	readonly rootItem: LayoutItem;

	/** indicates if layout using flexbox to drive height of item views (rather than using JS to calculate) */
	readonly useFlexHeight: boolean;

	/** cache for layout views; NOTE: needed so long as rendering views using Dojo */
	readonly viewParams: Record<string, any> = {
		cacheView: new StoreCache(),
	};

	/** set of view ids that have been selected (viewed) */
	private readonly viewedSet = observable.set<string>();

	/** traverse items to get flattened array of all items */
	@computed get allItems(): LayoutItem[] {
		const all = [];
		this.rootItem.children.forEach((item) => item.collectItems(all));
		return all;
	}

	/** indicates if layout contains tabs */
	get hasTabs(): boolean {
		return !!this.rootItem.children.length;
	}

	/** is layout read only */
	get readOnly(): boolean {
		return this._readOnly;
	}

	/** root node for layout */
	get rootNode(): C8Node | null {
		return this.rootItem.node;
	}

	/** root url for layout */
	get rootUrl(): cnl {
		return this.rootItem.options.url;
	}

	/** selected item */
	get selectedItem(): LayoutItem {
		return this._selectedItem;
	}

	/** array of items to reach the selected item */
	@computed get selectedItemsWithTabs(): LayoutItem[] {
		const { rootItem, _selectedItem } = this;
		const a = [];

		if (_selectedItem) {
			let item = _selectedItem.parent;

			// if selected item also has tabs, then include it as well
			if (_selectedItem.children.length) {
				a.push(_selectedItem);
			}

			while (item) {
				a.unshift(item);
				item = item.parent;
			}
		} else {
			a.push(rootItem);
		}

		return a;
	}

	/** root node type for layout */
	get type(): string {
		return this.rootNode?.$Type;
	}

	/** list of all viewed items (that have been selected at one point) */
	@computed get viewedItems(): LayoutItem[] {
		const { allItems, viewedSet } = this;
		return allItems.filter((item) => viewedSet.has(item.uniqueId));
	}

	/** views in layout (root views - does not include nested views) */
	@computed get views(): ViewWidget[] {
		const { viewedItems, rootItem, _selectedItem } = this;
		let a: ViewWidget[];

		if (_selectedItem) {
			a = viewedItems.map((item) => item.view);
		} else {
			a = [rootItem.view];
		}

		return _.compact(a);
	}

	/** list of visible viewed items */
	@computed get visibleViewedItems(): LayoutItem[] {
		return this.viewedItems.filter((item) => item.visible);
	}

	/** add view to layout */
	addItem = (id: string, options?: LayoutItemOptions): LayoutItem => {
		return this.rootItem.addChild(id, options);
	};

	/** find item in layout by id */
	findItem = (id: string): LayoutItem => {
		return this.allItems.find((item) => item.id === id);
	};

	/** browser URL to link to item */
	getItemHref = (item: LayoutItem): string => {
		return getLocationHash({
			...this.locationParams,
			[LOCATION_TAB]: item.id,
		});
	};

	/** determine if given item is parent of the selected item (and thus must be expanded) */
	isParentOfSelectedItem = (item: LayoutItem): boolean => {
		let parent = this._selectedItem?.parent;

		while (parent) {
			if (parent === item) {
				return true;
			}

			parent = parent.parent;
		}

		return false;
	};

	/** resolve nodes for given items */
	resolveItems = async (items: LayoutItem[], dependencyPaths?: cnlPath[]): Promise<void> => {
		// traverse layout to find items that cannot be resolved
		const { cache, viewParams } = this;
		const urlPathMap: Map<string, cnlPath> = new Map();
		const addPath = (urlPath: cnlPath): void => {
			const [url, path] = urlPath;
			const key = `${url}-${path || ""}`;
			urlPathMap.set(key, urlPath);
		};

		// find all url-path pairs needed for items
		items.forEach((item) => {
			addPath(item.urlPath);
			item.rulePaths.forEach(addPath);
		});

		// add extra paths
		dependencyPaths?.forEach(addPath);

		const urlPaths = cache.getUnresolvedUrlPaths([...urlPathMap.values()]);

		if (urlPaths.length) {
			// request missing nodes
			const request = comm.getUrlPathsRequest(urlPaths);
			const response = await request.go();
			cache.cacheResponse(response);
			viewParams.cacheView.cacheResponse(response);
		}
	};

	/** set the selected item based on the given id(s); if they are not visible or not found, then resolve to first */
	resolveSelection = (targetIds?: string[]): LayoutItem | null => {
		// visibleItems is flattened array of view hierarchy
		const { allItems, rootItem, _setSelectedItem } = this;
		let selection: LayoutItem;

		// it's possible there is only a header view
		if (!allItems.length) {
			return null;
		}

		// find first visible selection based on target ids
		targetIds?.some((id) => {
			selection = allItems.find((item) => (item.id === id) && item.visibleWithParents);
			return selection;
		});

		// if item is already selected, then nothing further to be done
		if (selection?.selected) {
			return selection;
		}

		// if no selection, then default to root item
		selection ||= rootItem;

		// if selected item has children, then find descendant to select
		selection = selection.childToSelect;

		_setSelectedItem(selection);

		return selection;
	};

	/** set layout content as read-only so no actions should be allowed */
	@action setReadOnly = (readOnly: boolean, locationNode: C8Node, locationRevisionNode: C8Node): void => {
		const { views } = this;

		this._readOnly = readOnly;

		// TODO: when Dojo views are gone, then none of this should be necessary as actions on page should
		// probably react to read-only status from the property above rather than look to their view to do it
		const update = (view: ViewWidget): boolean => {
			view.cacheView.cacheNode(locationNode);
			view.cacheView.cacheNode(locationRevisionNode);
			view.forceReadOnly();
			return true;
		};

		for (const view of views) {
			update(view);
			view.traverseViews(update);
		}
	};

	/** validate that the current selection is still valid, and if not, then select a new one */
	validateSelection = (): void => {
		const { selectedItemsWithTabs, _selectedItem, _setSelectedItem } = this;

		// do nothing until there is a valid selection
		if (!_selectedItem) {
			return;
		}

		// find any invisible item among selected items
		const invisibleItem = [...selectedItemsWithTabs, _selectedItem].find((item) => !item.visible);
		let selection: LayoutItem;

		if (invisibleItem) {
			// select a visible sibling tab - make sure to unselect item first or else childToSelect will return it
			invisibleItem.unselect();
			selection = invisibleItem.parent.childToSelect;
		} else {
			// select a child tab now that it is no longer a leaf (child views became visible)
			selection = _selectedItem.childToSelect;
		}

		if (selection) {
			// select new item
			_setSelectedItem(selection);
		} else {
			// no item to select
			console.assert(false);
		}
	};

	/** make item the current selection */
	@action private _setSelectedItem = (item: LayoutItem): void => {
		const { viewedItems, viewedSet, _selectedItem } = this;

		if (item === _selectedItem) {
			return;
		}

		// tell views under existing tab that they will no longer be displayed
		_selectedItem?.view?.setTabNotSelected();

		// select item and all parents
		let { parent } = item;
		const { uniqueId, view } = item;

		item.select();

		while (parent) {
			// expand all parent items
			// TODO - remove if left pane design is removed
			parent.setCollapsed(false);

			parent.select();
			parent = parent.parent;
		}

		// validate that views in set are still visible
		for (const viewedItem of viewedItems) {
			if (!viewedItem.visible) {
				viewedSet.delete(viewedItem.uniqueId);
			}
		}

		// track all non-tab views that have been selected
		if (!item.children.length) {
			viewedSet.add(uniqueId);
		}

		// notify view, and all child views, when selected tab changes
		view?.setTabSelected();

		this._selectedItem = item;
	};

	constructor(
		rootUrl: cnl,
		hostViewId: string,
		locationParams?: Record<string, string>,
		historyKey?: string,
		headerViewId?: string,
		useFlexHeight = false,
		cache = new WorkingCache(),
	) {
		super();
		makeObservable(this);

		this.cache = cache;
		this.historyKey = historyKey;
		this.locationParams = locationParams;
		this.useFlexHeight = useFlexHeight;

		this.rootItem = new LayoutItem(hostViewId, cache, { url: rootUrl });
		this.rootItem.headerViewId = headerViewId;
		this.rootItem.resolveView("", this.rootNode.$Type);

		// NOTE: this is necessary for WorkingCache to have access to latest updates to nodes in view
		this.viewParams.cacheView.mirrorCache = cache;

		// monitor tab selection as it may need to change as the cache changes (i.e. Manage Team switch can remove tabs)
		this.trackReaction(autorun(this.validateSelection));
	}
}
